import React from "react";
import { useTranslation } from "react-i18next";
import { PickupPointOption } from "../../../../api/meLocation.service";

export interface PlacePointPickerProps {
  field: "pickup" | "dropoff";
  placeName: string;
  points: PickupPointOption[];
  /** The point being resolved to coordinates, if any. */
  resolvingPointId: string | null;
  error: boolean;
  onChoose: (point: PickupPointOption) => void;
  /** Drop-off only: use the place's main address instead of a point. */
  onUseMainAddress?: () => void;
  /** Keeps focus on the input while a choice is tapped. */
  onMouseDown: (event: React.MouseEvent) => void;
}

const rowClass =
  "flex w-full items-center gap-3 rounded-xl p-2.5 text-left hover:bg-[#F2F3F3] focus-visible:bg-[#F2F3F3] focus-visible:outline-none disabled:opacity-60";

// A place with several official points (a mall, an airport): the rider chooses one before the
// route and prices show. A drop-off can also keep the place's main address.
const PlacePointPicker: React.FC<PlacePointPickerProps> = ({
  field,
  placeName,
  points,
  resolvingPointId,
  error,
  onChoose,
  onUseMainAddress,
  onMouseDown,
}) => {
  const { t } = useTranslation("hailing");
  const copy = field === "pickup" ? "steps.where.pickupPoints" : "steps.where.dropoffPoints";
  return (
    <section
      aria-label={t(`${copy}.title`)}
      className="mt-3 rounded-2xl bg-white p-3 shadow-[0_8px_24px_rgba(15,21,26,0.1)]"
    >
      <h2 className="text-[15px] font-semibold text-ink-100">{t(`${copy}.title`)}</h2>
      <p className="mt-0.5 text-[13px] leading-[1.45] text-[#6B7075]">
        {t(`${copy}.subtitle`, { place: placeName })}
      </p>
      <div className="mt-2 flex flex-col">
        {points.map((point) => (
          <button
            key={point.placeId}
            type="button"
            onMouseDown={onMouseDown}
            onClick={() => onChoose(point)}
            disabled={resolvingPointId !== null}
            className={rowClass}
          >
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#ECECEC]">
              <span className="font-material-symbols text-[18px] leading-none text-ink-100">
                {resolvingPointId === point.placeId ? "progress_activity" : "hail"}
              </span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-medium text-ink-100">
                {point.displayName}
              </span>
              {point.formattedAddress && point.formattedAddress !== point.displayName ? (
                <span className="mt-0.5 block truncate text-[12px] text-[#6B7075]">
                  {point.formattedAddress}
                </span>
              ) : null}
            </span>
          </button>
        ))}
        {onUseMainAddress && (
          <button
            type="button"
            onMouseDown={onMouseDown}
            onClick={onUseMainAddress}
            disabled={resolvingPointId !== null}
            className={rowClass}
          >
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#ECECEC]">
              <span className="font-material-symbols text-[18px] leading-none text-ink-100">
                location_on
              </span>
            </span>
            <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-ink-100">
              {t(`${copy}.mainAddress`, { place: placeName })}
            </span>
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-2 px-1 text-[13px] text-danger-ink">
          {t(`${copy}.error`)}
        </p>
      )}
    </section>
  );
};

export default PlacePointPicker;
